// Unit conversion constants
export const MM_PER_INCH = 25.4;
export const GRAMS_PER_OZ = 28.3495;

// Cookie helper functions
export const setCookie = (name, value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/`;
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Shared calculation utilities
export const round = (number, decimals = 2) => {
  if (isNaN(number) || number === null) {
    return 0; // Or handle as appropriate, return 0 for calculations
  }
  const factor = Math.pow(10, decimals);
  return Math.round(number * factor) / factor;
};

export const formatNumber = (number) => {
  const rounded = round(number);
  if (rounded >= 1000) {
    return rounded.toLocaleString('en-US');
  }
  return rounded.toString();
};

export const convertLength = (value, toMetric) => {
  const num = parseFloat(value);
  if (isNaN(num) || value === '') return '';
  return round(toMetric ? num * MM_PER_INCH : num / MM_PER_INCH);
};

export const convertWeight = (value, toMetric) => {
  const num = parseFloat(value);
  if (isNaN(num) || value === '') {
    return '';
  }
  // Remove the round() call here to return the precise value
  const result = toMetric ? num * GRAMS_PER_OZ : num / GRAMS_PER_OZ;
  return result;
};

// Function to calculate the area based on shape type and dimensions
export const calculateArea = (shapeType, width, length, diameter, overrideArea) => {
  const numWidth = parseFloat(width) || 0;
  const numLength = parseFloat(length) || 0;
  const numDiameter = parseFloat(diameter) || 0;

  // Ensure dimensions are non-negative
  if (numWidth < 0 || numLength < 0 || numDiameter < 0) {
    return 0; // Or NaN, depending on desired handling
  }

  switch (shapeType) {
    case 'rectangle':
      return numWidth * numLength;
    case 'square':
      // For square, width and length should ideally be the same. Use width.
      return numWidth * numWidth;
    case 'circle':
      if (numDiameter <= 0) return 0;
      const radius = numDiameter / 2;
      return Math.PI * radius * radius;
    case 'triangle':
      // Assuming width is base and length is height for triangle
      return 0.5 * numWidth * numLength;
    case 'custom':
       // If 'custom', assume width is provided as the area directly
      return numWidth;
    case 'override':
    case 'photo':
      // User-supplied area override (sq inches canonical)
      return parseFloat(overrideArea) || 0;
    default:
      return 0; // Default case if shape type is missing or unrecognized
  }
};

/**
 * Pure, DOM-free icing calculation.
 *
 * All inputs and outputs are in CANONICAL units:
 *   lengths → inches,  weights → grams,  area → square inches
 *
 * @param {object} params
 * @param {string}  params.shapeType       - 'rectangle'|'square'|'circle'|'triangle'|'override'|'photo'
 * @param {number}  params.width           - inches (rectangle/square/triangle base)
 * @param {number}  params.length          - inches (rectangle/triangle height)
 * @param {number}  params.diameter        - inches (circle)
 * @param {number}  params.overrideArea    - sq inches (override/photo shapes)
 * @param {number}  params.refWidth        - reference cookie width, inches
 * @param {number}  params.refLength       - reference cookie length, inches
 * @param {number}  params.refWeightNoIcing - reference cookie weight without icing, grams
 * @param {number}  params.refWeight       - reference cookie weight with icing, grams
 * @param {number}  [params.count=1]       - number of cookies
 *
 * @returns {{ area: number, icingPerSqIn: number, icingGrams: number }}
 *   area         — target cookie area in sq inches
 *   icingPerSqIn — density derived from reference (g / in²)
 *   icingGrams   — total icing needed in grams for `count` cookies
 */
export const calculateIcingGrams = ({
  shapeType,
  width = 0,
  length = 0,
  diameter = 0,
  overrideArea = 0,
  refWidth,
  refLength,
  refWeightNoIcing,
  refWeight,
  count = 1,
}) => {
  const refArea = calculateArea('rectangle', parseFloat(refWidth) || 0, parseFloat(refLength) || 0);
  const refIcing = (parseFloat(refWeight) || 0) - (parseFloat(refWeightNoIcing) || 0);
  const icingPerSqIn = refArea > 0 && refIcing > 0 ? refIcing / refArea : 0;

  const area = calculateArea(shapeType, width, length, diameter, overrideArea);
  const icingGrams = area * icingPerSqIn * (count || 1);

  return { area, icingPerSqIn, icingGrams };
};

// ── Detail/Floral/Other extra-icing amounts ─────────────────────────────
// Stored per plan as { detail: number[], floral: number[], other: number[] },
// one grams value per color, parallel to colorNames. Older saved plans stored
// a single flat number[] (the old "Detail/Floral Consistency" field) — that
// legacy shape is migrated into the "detail" row so existing data isn't lost.
export const DETAIL_CATEGORIES = ['detail', 'floral', 'other'];

export const normalizeDetailAmounts = (saved, numColors) => {
  const emptyArr = () => Array(numColors).fill(0);
  const padArr = (arr) => {
    const out = emptyArr();
    if (Array.isArray(arr)) {
      for (let i = 0; i < Math.min(arr.length, numColors); i++) out[i] = Number(arr[i]) || 0;
    }
    return out;
  };
  if (Array.isArray(saved)) {
    // Legacy flat array — migrate into the Detail row
    return { detail: padArr(saved), floral: emptyArr(), other: emptyArr() };
  }
  if (saved && typeof saved === 'object') {
    return { detail: padArr(saved.detail), floral: padArr(saved.floral), other: padArr(saved.other) };
  }
  return { detail: emptyArr(), floral: emptyArr(), other: emptyArr() };
};

// Sum of all 3 categories for a single color index (grams)
export const sumDetailAmountsForColor = (detailAmounts, index) => {
  if (!detailAmounts) return 0;
  return DETAIL_CATEGORIES.reduce((s, key) => s + (Number(detailAmounts[key]?.[index]) || 0), 0);
};

// Grand total across every category and every color (grams)
export const sumAllDetailAmounts = (detailAmounts) => {
  if (!detailAmounts) return 0;
  return DETAIL_CATEGORIES.reduce((s, key) => {
    const arr = detailAmounts[key] || [];
    return s + arr.reduce((s2, g) => s2 + (Number(g) || 0), 0);
  }, 0);
};

export const validateAndFormatValue = (value, isCount = false) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return 0;

  const positiveValue = Math.abs(numValue);
  
  if (isCount) {
    return Math.max(1, Math.ceil(positiveValue));
  }
  
  return Math.round(positiveValue * 100) / 100;
};
